import { getAuth } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { getToken } from "firebase/app-check";

import store from "../../redux/store";
import { setUserToken } from "../../redux/slices/userSlice";
import { firestore, getAppCheckInstance, server } from "../../config/ConfigFirebase";
import { debugLog } from "../../functions";
import { normalizeNit } from "../../components/entities/entityLogic";
import { getEntityConfig } from "./EntityConfigService";
import { UploadFile } from "./UploadFileService";

// Las entidades con las que la Dirección de Extensión firma convenios viven en
// su propia colección de primer nivel, keyed por el NIT normalizado (sólo
// dígitos, sin DV) — la misma decisión que en agreements (keyed por código) y
// applicants (por nuip): el NIT es único por definición, así que sirve de llave
// y ahorra una transacción de unicidad.
// Las lecturas van directo a Firestore; las mutaciones pasan por la función
// handleEntity, que revalida permisos y deja bitácora.
const COLLECTION = "entities";

// URL de la Cloud Function del entorno actual. `server` es
// `{region}-{projectId}`, así que cada entorno resuelve a las funciones de SU
// propio proyecto sin URLs hardcodeadas.
export const entityFunctionUrl = (fn) => `https://${server}.cloudfunctions.net/${fn}`;

// Los Timestamp de Firestore no son serializables para Redux.
const toIso = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v;
  if (v.toDate) return v.toDate().toISOString();
  if (v instanceof Date) return v.toISOString();
  return null;
};

const mapEntity = (snap) => {
  const data = snap.data();
  return {
    ...data,
    nit: data.nit || snap.id,
    createdAt: toIso(data.createdAt),
    updatedAt: toIso(data.updatedAt),
    deletedAt: toIso(data.deletedAt),
  };
};

// Llamada con Bearer token a handleEntity. Igual que AgreementService: ante un
// 401 se refresca el token cacheado en Redux.
const handleEntity = async (token, body) => {
  const response = await fetch(entityFunctionUrl("handleEntity"), {
    method: "post",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  });

  if (response.status === 401) {
    const auth = getAuth();
    const refreshed = await auth.currentUser.getIdToken();
    store.dispatch(setUserToken(refreshed));
  }

  return response;
};

// Normaliza la respuesta de una mutación a una forma única para los formularios:
// { ok, data, error, errors } — `errors` mapea campo → código de validación, que
// EntityForm traduce con ENTITY_ERROR_LABELS.
const asResult = async (response) => {
  const json = await response.json().catch(() => ({}));
  if (json && json.result) return { ok: true, data: json.body || null, error: null, errors: {} };
  return {
    ok: false,
    data: null,
    error: (json && json.error) || "UNKNOWN",
    errors: (json && json.body && json.body.errors) || {},
  };
};

// --- Lecturas (directo desde Firestore) --------------------------------------

// Página con cursor para el listado. Se pide un elemento de más para saber si
// hay siguiente página sin una segunda consulta. `deletedAt == null` excluye las
// entidades borradas lógicamente.
export const fetchEntityPage = async ({
  entityType,
  status,
  cursor,
  pageSize = 25,
} = {}) => {
  try {
    const constraints = [where("deletedAt", "==", null)];
    if (entityType) constraints.push(where("entityType", "==", entityType));
    if (status) constraints.push(where("status", "==", status));
    constraints.push(orderBy("createdAt", "desc"));
    if (cursor) constraints.push(startAfter(cursor));
    constraints.push(limit(pageSize + 1));

    const snap = await getDocs(query(collection(firestore, COLLECTION), ...constraints));
    const pageDocs = snap.docs.slice(0, pageSize);
    return {
      items: pageDocs.map(mapEntity),
      lastDoc: pageDocs[pageDocs.length - 1] || null,
      hasMore: snap.docs.length > pageSize,
    };
  } catch (e) {
    console.error("fetchEntityPage error:", e);
    // `error` viaja al listado para que un fallo (índice construyéndose,
    // permisos) no se muestre como "sin resultados".
    return { items: [], lastDoc: null, hasMore: false, error: e?.code || "unknown" };
  }
};

export const getEntity = async (nit) => {
  try {
    const snap = await getDoc(doc(firestore, COLLECTION, normalizeNit(nit)));
    if (!snap.exists()) return null;
    const entity = mapEntity(snap);
    // Una entidad borrada no existe para las vistas.
    return entity.deletedAt ? null : entity;
  } catch (e) {
    console.error("getEntity error:", e);
    return null;
  }
};

// Las entidades activas, en la forma mínima que necesita el selector del
// formulario de convenios. Ordenadas por razón social, que es como las busca
// quien las escoge — y no por fecha de registro, que ahí no significa nada.
export const fetchEntityOptions = async ({ max = 500 } = {}) => {
  try {
    const snap = await getDocs(query(
      collection(firestore, COLLECTION),
      where("deletedAt", "==", null),
      orderBy("legalName", "asc"),
      limit(max)
    ));
    return {
      items: snap.docs
        .map((d) => {
          const data = d.data();
          return {
            nit: data.nit || d.id,
            verificationDigit: data.verificationDigit ?? null,
            legalName: data.legalName || "",
            entityType: data.entityType || null,
            status: data.status || null,
          };
        })
        // Una entidad inactiva no puede recibir un convenio nuevo, pero sí
        // aparece en los que ya tiene: el filtro va aquí y no en la consulta
        // para no pedir otro índice por un puñado de documentos.
        //
        // PENDING se esconde por la misma razón que lo rechaza el backend
        // (loadEntityForWrite en handleAgreement): se registró sola por el
        // portal y nadie la ha revisado. Ofrecerla en el selector sería invitar
        // a un error que la función va a rechazar de todos modos.
        .filter((e) => e.status !== "INACTIVE" && e.status !== "PENDING"),
      error: null,
    };
  } catch (e) {
    console.error("fetchEntityOptions error:", e);
    return { items: [], error: e?.code || "unknown" };
  }
};

// Los convenios de una entidad. Es la consulta que justifica el índice
// (deletedAt, entityNit, createdAt DESC).
export const fetchEntityAgreements = async (nit, { max = 50 } = {}) => {
  try {
    const snap = await getDocs(query(
      collection(firestore, "agreements"),
      where("deletedAt", "==", null),
      where("entityNit", "==", normalizeNit(nit)),
      orderBy("createdAt", "desc"),
      limit(max)
    ));
    return {
      items: snap.docs.map((d) => {
        const data = d.data();
        return { ...data, code: data.code || d.id, createdAt: toIso(data.createdAt) };
      }),
      error: null,
    };
  } catch (e) {
    console.error("fetchEntityAgreements error:", e);
    return { items: [], error: e?.code || "unknown" };
  }
};

// --- Mutaciones (vía handleEntity) -------------------------------------------

export const createEntity = async (entity, user) => {
  try {
    const response = await handleEntity(user.token, { action: "CREATE_ENTITY", entity });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

// `entity` es un patch: sólo los campos que cambian. El servidor lo fusiona con
// el documento guardado antes de validar e ignora los campos inmutables (NIT,
// documentos, conteo de convenios).
export const updateEntity = async (nit, entity, user) => {
  try {
    const response = await handleEntity(user.token, {
      action: "UPDATE_ENTITY",
      nit: normalizeNit(nit),
      entity,
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

// Borrado lógico. El servidor lo rechaza si la entidad sostiene algún convenio:
// el vínculo del convenio a su entidad es obligatorio y no puede quedar colgando.
export const deleteEntity = async (nit, user) => {
  try {
    const response = await handleEntity(user.token, {
      action: "DELETE_ENTITY",
      nit: normalizeNit(nit),
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

// --- Documentos ---------------------------------------------------------------

const extensionOf = (name) => {
  const match = /\.([A-Za-z0-9]+)$/.exec(String(name || ""));
  return match ? match[1].toLowerCase() : "bin";
};

// Carga un documento de la entidad. Son DOS pasos a propósito, que es el patrón
// del personal interno (radicados, contratos): el archivo va al bucket con
// UploadFile —que ya resuelve el troceado de los archivos grandes y el token—, y
// sólo después su ficha entra por handleEntity, que revalida el catálogo y deja
// bitácora. Si el segundo paso falla, en el bucket queda un objeto huérfano pero
// el documento de la entidad no se mueve, que es el lado seguro del error.
export const uploadEntityDocument = async ({
  nit,
  type,
  file,
  expiryDate = null,
  // Fecha de EXPEDICIÓN. El servidor la exige cuando el tipo la declara
  // (hasIssueDate) y la ignora cuando no, así que mandarla siempre es inocuo.
  issuedAt = null,
  replaceFileId = null,
  user,
}) => {
  try {
    const cfg = await getEntityConfig();
    const bucket = cfg.storageBucket;
    if (!bucket) return { ok: false, data: null, error: "UPLOAD_FAILED", errors: {} };

    const cleanNit = normalizeNit(nit);
    // Cada archivo en su propia ruta versionada: un reemplazo nunca pisa los
    // bytes del anterior, que siguen referenciados desde versions[].
    const stamp = `${Date.now()}`;
    const path = `entities/${cleanNit}/documents/${type}/${stamp}.${extensionOf(file.name)}`;

    const uploaded = await UploadFile(user, file, bucket, path);
    if (!uploaded || !uploaded.result) {
      return { ok: false, data: null, error: "UPLOAD_FAILED", errors: {} };
    }

    const response = await handleEntity(user.token, {
      action: "ADD_DOCUMENT",
      nit: cleanNit,
      type,
      expiryDate,
      issuedAt,
      replaceFileId,
      file: {
        name: file.name,
        path,
        url: uploaded.downloadUrl || null,
        hash: uploaded.fileHash || null,
        mimeType: file.type || null,
        size: file.size || 0,
      },
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

export const deleteEntityDocument = async ({ nit, type, fileId, user }) => {
  try {
    const response = await handleEntity(user.token, {
      action: "DELETE_DOCUMENT",
      nit: normalizeNit(nit),
      type,
      fileId,
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

export const validateEntityDocument = async ({ nit, type, fileId, status, comments = null, user }) => {
  try {
    const response = await handleEntity(user.token, {
      action: "VALIDATE_DOCUMENT",
      nit: normalizeNit(nit),
      type,
      fileId,
      status,
      comments,
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

// Bitácora de la entidad (colección entityAudit), más reciente primero. Sólo
// ADMIN. Devuelve [] ante cualquier fallo para que el drawer muestre "sin
// eventos" en vez de romperse.
export const getEntityLogs = async (nit, user) => {
  try {
    const response = await handleEntity(user.token, {
      action: "GET_ENTITY_LOGS",
      nit: normalizeNit(nit),
    });
    const json = await response.json();
    return json.result ? (json.body || []) : [];
  } catch (error) {
    debugLog(error);
    return [];
  }
};

// --- Acciones de gestión ------------------------------------------------------

// Aprueba una entidad que llegó por el portal público: pasa de PENDING a ACTIVE
// y queda disponible para convenios. Requiere el permiso entity.approve.
export const approveEntity = async (nit, user) => {
  try {
    const response = await handleEntity(user.token, {
      action: "APPROVE_ENTITY",
      nit: normalizeNit(nit),
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

// La rechaza (queda INACTIVE con el motivo guardado). El motivo es obligatorio y
// el servidor exige al menos 5 caracteres: rechazar sin decir por qué deja a la
// entidad adivinando qué corregir.
export const rejectEntity = async (nit, reason, user) => {
  try {
    const response = await handleEntity(user.token, {
      action: "REJECT_ENTITY",
      nit: normalizeNit(nit),
      reason,
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

// Sube el logo al bucket y guarda su ruta. Mismo patrón de dos pasos que
// uploadEntityDocument. `file` en null quita el logo.
export const setEntityLogo = async ({ nit, file = null, user }) => {
  try {
    const cleanNit = normalizeNit(nit);
    let logoPath = null;

    if (file) {
      const cfg = await getEntityConfig();
      const bucket = cfg.storageBucket;
      if (!bucket) return { ok: false, data: null, error: "UPLOAD_FAILED", errors: {} };
      logoPath = `entities/${cleanNit}/logo-${Date.now()}.${extensionOf(file.name)}`;
      const uploaded = await UploadFile(user, file, bucket, logoPath);
      if (!uploaded || !uploaded.result) {
        return { ok: false, data: null, error: "UPLOAD_FAILED", errors: {} };
      }
    }

    const response = await handleEntity(user.token, {
      action: "SET_LOGO",
      nit: cleanNit,
      logoPath,
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

// --- Contactos ----------------------------------------------------------------

// Alta, edición, primario y baja de un correo o un teléfono, desde la ficha del
// gestor. `channel` es 'EMAIL' | 'PHONE'.
//
// Lo que un gestor añade queda SIN verificar y, por tanto, fuera de los índices
// con los que se entra al portal: puede teclear una dirección, no probar que
// funciona. Verificado sólo lo pone el portal, que es quien ve el código.
export const addEntityContact = async ({ nit, channel, contact, user }) => {
  try {
    const response = await handleEntity(user.token, {
      action: "ADD_CONTACT",
      nit: normalizeNit(nit),
      channel,
      contact,
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

export const updateEntityContact = async ({ nit, channel, contactId, contact, setPrimary = false, user }) => {
  try {
    const response = await handleEntity(user.token, {
      action: "UPDATE_CONTACT",
      nit: normalizeNit(nit),
      channel,
      contactId,
      contact,
      setPrimary,
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

export const deleteEntityContact = async ({ nit, channel, contactId, user }) => {
  try {
    const response = await handleEntity(user.token, {
      action: "DELETE_CONTACT",
      nit: normalizeNit(nit),
      channel,
      contactId,
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

// --- Portal público (vía intakeEntity / entityOtp) ----------------------------

// Token de App Check fresco (forceRefresh), el mismo patrón de ApplicantService.
async function appCheckToken() {
  const instance = getAppCheckInstance();
  if (!instance) throw new Error("App Check no está inicializado");
  const { token } = await getToken(instance, true);
  if (!token) throw new Error("Token de App Check no disponible");
  return token;
}

const intakeUrl = () =>
  process.env.REACT_APP_ENTITY_INTAKE_URL || entityFunctionUrl("intakeEntity");
const otpUrl = () =>
  process.env.REACT_APP_ENTITY_OTP_URL || entityFunctionUrl("entityOtp");

// Los archivos que la entidad sube desde el portal público. El logo va en su
// propio campo del multipart para que no entre en el emparejamiento
// files[]/types[] ni en el mapa de documentos: es la imagen de la ficha, no un
// documento del catálogo.
//
// `files` es [{ type, file, expiryDate?, issuedAt?, replaceFileId? }]. Un tipo
// que declara `hasIssueDate` (el RUT) exige `issuedAt`: la función rechaza la
// carga sin ella, igual que hace con `expiryDate` cuando el tipo tiene
// vigencia. `sessionToken` es lo
// que prueba que quien llama ya entró a ESTA ficha por OTP, y es lo que
// distingue una edición legítima de un intento de registrar un NIT ajeno.
export const registerEntity = async ({
  data,
  founder = null,
  files = [],
  logo = null,
  removeLogo = false,
  idempotencyKey,
  phoneToken,
  sessionToken,
} = {}) => {
  try {
    const token = await appCheckToken();
    const payload = {
      entity: data,
      // Quien registra la entidad. Viaja aparte de `entity` porque no es parte
      // de la ficha: con esto la función crea su usuario en `users` y devuelve
      // el token con el que entra de una vez.
      ...(founder ? { founder } : {}),
      // Un descriptor por archivo, alineado por índice con files[]. Viaja dentro
      // del JSON y no como campos sueltos porque busboy topa la petición en
      // MAX_FIELDS: tres arreglos paralelos se comerían ese presupuesto.
      fileMeta: files.map(({ type, expiryDate, issuedAt, replaceFileId }) => ({
        type,
        ...(expiryDate ? { expiryDate } : {}),
        ...(issuedAt ? { issuedAt } : {}),
        // Nombra el archivo guardado al que reemplaza esta carga. Sin él, la
        // carga AÑADE uno nuevo y el rechazado se queda en su sitio.
        ...(replaceFileId ? { replaceFileId } : {}),
      })),
      ...(removeLogo ? { removeLogo: true } : {}),
      meta: { source: "entity-portal", version: "1.0" },
    };

    const fd = new FormData();
    fd.append("payload", JSON.stringify(payload));
    files.forEach(({ type, file }) => {
      fd.append("files[]", file);
      fd.append("types[]", type);
    });
    // Su propio campo, añadido al final para que nunca desplace el
    // emparejamiento files[]/types[] del que depende el backend.
    if (logo) fd.append("logo", logo, logo.name || "logo.png");

    const headers = { "X-Firebase-AppCheck": token };
    if (idempotencyKey) headers["X-Idempotency-Key"] = idempotencyKey;
    // ID token de Firebase Phone Auth que prueba el celular. Va en SU PROPIA
    // cabecera y no en Authorization, que el backend lee como token de personal
    // y usaría para saltarse la puerta de verificación.
    if (phoneToken) headers["X-Phone-Token"] = phoneToken;
    if (sessionToken) headers["X-Entity-Session"] = sessionToken;

    const response = await fetch(intakeUrl(), { method: "POST", body: fd, headers });
    const json = await response.json().catch(() => ({}));
    return { ok: response.ok, status: response.status, json };
  } catch (error) {
    debugLog(error);
    return { ok: false, status: 0, json: { result: false, error: error.message } };
  }
};

const postOtp = async (body) => {
  const token = await appCheckToken();
  const response = await fetch(otpUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Firebase-AppCheck": token },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => ({}));
  return { ok: response.ok, status: response.status, json };
};

// Pide un código al correo indicado.
export const sendEntityOtp = async ({ recipient }) => {
  try { return await postOtp({ action: "SEND", recipient }); }
  catch (error) { debugLog(error); return { ok: false, status: 0, json: { result: false, error: error.message } }; }
};

// Verifica el código. Devuelve { nit, sessionToken } cuando el correo ya
// pertenece a una entidad registrada; en un registro nuevo ambos vienen en null
// y el token lo emite el intake.
export const verifyEntityOtp = async ({ recipient, code }) => {
  try { return await postOtp({ action: "VERIFY", recipient, code }); }
  catch (error) { debugLog(error); return { ok: false, status: 0, json: { result: false, error: error.message } }; }
};

// Entrada por celular: el cliente ya verificó con Firebase Phone Auth y aquí
// canjea ese ID token por la sesión del portal.
export const resolveEntityPhone = async ({ phoneToken }) => {
  try { return await postOtp({ action: "RESOLVE_PHONE", phoneToken }); }
  catch (error) { debugLog(error); return { ok: false, status: 0, json: { result: false, error: error.message } }; }
};

// Pre-chequeo para que el formulario falle rápido —antes de gastar un correo o
// un SMS— si el NIT ya existe o el contacto es de otra entidad.
export const checkEntityContact = async ({ nit, email, phone }) => {
  try {
    const { json } = await postOtp({
      action: "CHECK_CONTACT",
      nit: nit ? normalizeNit(nit) : "",
      email: email || "",
      phone: phone || "",
    });
    return {
      emailInUse: !!json.emailInUse,
      phoneInUse: !!json.phoneInUse,
      nitInUse: !!json.nitInUse,
      // Ya entra a la aplicación con ese correo (funcionario u otra empresa).
      emailHasAccount: !!json.emailHasAccount,
    };
  } catch (error) {
    debugLog(error);
    // Ante un fallo de red no se bloquea el formulario: el intake vuelve a
    // exigir la unicidad por su cuenta, así que esto es comodidad, no la puerta.
    return { emailInUse: false, phoneInUse: false, nitInUse: false, emailHasAccount: false };
  }
};

// Los objetos del bucket se guardan publicRead, así que no hay que firmar nada.
// Misma forma que applicantDocPublicUrl.
export const entityFilePublicUrl = (bucketName, filePath) =>
  bucketName && filePath
    ? `https://storage.googleapis.com/${bucketName}/${filePath}`
    : null;

// --- Entity staff -------------------------------------------------------------
//
// The same actions serve both audiences: the company user (who can only touch
// their own, because handleEntity forces their NIT) and the internal manager
// (who passes the NIT, backed by their entity.* permission). That is why `nit`
// travels in the body even though for a company the backend ignores and replaces
// it.

const memberAction = async (user, body) => {
  try {
    const response = await handleEntity(user.token, body);
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, data: null, error: "NETWORK", errors: {} };
  }
};

// { members: [...], invites: [...] }
export const listEntityMembers = async (nit, user) =>
  memberAction(user, { action: "LIST_MEMBERS", nit: normalizeNit(nit) });

export const inviteEntityMember = async ({ nit, email, role }, user) =>
  memberAction(user, { action: "INVITE_MEMBER", nit: normalizeNit(nit), email, role });

// Used by the MANAGER so an already-registered entity gets its first account.
export const inviteEntityAdmin = async ({ nit, email }, user) =>
  memberAction(user, { action: "INVITE_ENTITY_ADMIN", nit: normalizeNit(nit), email });

export const resendEntityInvite = async ({ nit, inviteId }, user) =>
  memberAction(user, { action: "RESEND_INVITE", nit: normalizeNit(nit), inviteId });

export const revokeEntityInvite = async ({ nit, inviteId }, user) =>
  memberAction(user, { action: "REVOKE_INVITE", nit: normalizeNit(nit), inviteId });

export const setEntityMemberRole = async ({ nit, userId, role }, user) =>
  memberAction(user, { action: "SET_MEMBER_ROLE", nit: normalizeNit(nit), userId, role });

export const revokeEntityMember = async ({ nit, userId }, user) =>
  memberAction(user, { action: "REVOKE_MEMBER", nit: normalizeNit(nit), userId });

// Errors specific to staff administration.
export const ENTITY_MEMBER_ERROR_LABELS = {
  LAST_ADMIN:
    "No puedes dejar la empresa sin administradores. Nombra a otro antes de hacer este cambio.",
  ALREADY_MEMBER: "Esa persona ya hace parte de la empresa.",
  EMAIL_IN_USE: "Ese correo ya pertenece a otra empresa.",
  EMAIL_IS_STAFF: "Ese correo ya tiene una cuenta interna. Usa otro.",
  CANNOT_REVOKE_SELF: "No puedes retirarte a ti mismo.",
  INVITE_NOT_PENDING: "Esa invitación ya no está pendiente.",
  NOT_FOUND: "No se encontró el registro.",
  NETWORK: "No hay conexión con el servidor. Intenta de nuevo.",
};

export const memberErrorLabel = (code) =>
  ENTITY_MEMBER_ERROR_LABELS[code] || "No fue posible completar la operación.";
